import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Award, Sparkles, Check, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";

import { api } from "@/api/client";

/**
 * ComicBadge — appears on the last page of the reader.
 * - Asks for the vendor's name (mirrors the exam flow so we can match records).
 * - "Reclamar mi badge" → POSTs /public/comics/:slug/read, checks exam status,
 *   then reveals a digital badge with the vendor's name + timestamp.
 * - If they also passed the linked exam, badge upgrades to GOLD with confetti.
 */
export default function ComicBadge({ comic, lang, testid = "comic-badge" }) {
  const storedName = typeof window !== "undefined"
    ? localStorage.getItem("canales_reader_name") || ""
    : "";
  const [name, setName] = useState(storedName);
  const [status, setStatus] = useState(null); // { read, exam_passed, best_score }
  const [claiming, setClaiming] = useState(false);
  const confettiRef = useRef(null);

  const alreadyLocal = typeof window !== "undefined"
    ? localStorage.getItem(`canales_comic_read_${comic.slug}`) === "1"
    : false;

  const load = async (n) => {
    if (!n.trim()) return;
    try {
      const { data } = await api.get(`/public/comics/${comic.slug}/badge`, {
        params: { name: n.trim(), exam_slug: comic.exam_slug },
      });
      setStatus(data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    if (storedName) load(storedName);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const claim = async () => {
    if (!name.trim()) return;
    setClaiming(true);
    try {
      await api.post(`/public/comics/${comic.slug}/read`, {
        slug: comic.slug,
        employee_name: name.trim(),
      });
      localStorage.setItem("canales_reader_name", name.trim());
      localStorage.setItem(`canales_comic_read_${comic.slug}`, "1");
      await load(name);
    } finally {
      setClaiming(false);
    }
  };

  const isGold = status?.exam_passed;
  const isEarned = status?.read || alreadyLocal;

  // Confetti when badge appears
  useEffect(() => {
    if (!isEarned) return;
    const el = confettiRef.current;
    if (!el) return;
    const pieces = 24;
    el.innerHTML = "";
    for (let i = 0; i < pieces; i++) {
      const sp = document.createElement("span");
      sp.textContent = ["✦", "★", "◆", "●"][i % 4];
      sp.style.cssText = `position:absolute; left:${Math.random() * 100}%; top:-10px; font-size:${12 + Math.random() * 12}px; color:${["#FFD100", "#E3000F", "#005BBB", "#0d1a3f"][i % 4]}; animation:fall ${1500 + Math.random() * 1500}ms linear ${Math.random() * 400}ms forwards;`;
      el.appendChild(sp);
    }
    return () => { if (el) el.innerHTML = ""; };
  }, [isEarned, isGold]);

  const dateNow = useMemo(() => new Date().toLocaleDateString(lang === "es" ? "es-MX" : "en-US", {
    year: "numeric", month: "long", day: "numeric",
  }), [lang]);

  if (!isEarned) {
    return (
      <div className="brutal p-5 bg-canales-cream" data-testid={`${testid}-claim`}>
        <style>{`@keyframes fall { to { transform: translateY(240px) rotate(360deg); opacity: 0; } }`}</style>
        <div className="flex items-start gap-3">
          <Award className="w-8 h-8 text-canales-red flex-shrink-0" />
          <div className="flex-1">
            <div className="font-display font-black text-xl">
              {lang === "es" ? "¡Reclama tu badge digital!" : "Claim your digital badge!"}
            </div>
            <p className="font-body text-sm mt-1">
              {lang === "es"
                ? "Escribe tu nombre para guardar que leíste este cómic. Si además pasas el examen, el badge se vuelve DORADO."
                : "Enter your name so we save that you read this comic. Pass the exam too and the badge turns GOLD."}
            </p>
            <div className="mt-3 flex gap-2 flex-wrap">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={lang === "es" ? "Tu nombre" : "Your name"}
                data-testid={`${testid}-name`}
                className="brutal-sm bg-white px-3 py-2 flex-1 min-w-[180px] font-body"
              />
              <button
                onClick={claim}
                disabled={!name.trim() || claiming}
                data-testid={`${testid}-claim-btn`}
                className="brutal-btn bg-canales-red text-white px-4 py-2 font-display font-black flex items-center gap-2 disabled:opacity-50"
              >
                <Sparkles className="w-4 h-4" />
                {claiming ? (lang === "es" ? "Guardando…" : "Saving…") : (lang === "es" ? "Reclamar" : "Claim")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 180, damping: 18 }}
      className="brutal p-5 relative overflow-hidden"
      style={{ background: isGold ? "linear-gradient(135deg,#FFD100 0%,#FFA500 100%)" : "#eaf3ff" }}
      data-testid={`${testid}-earned`}
    >
      <style>{`@keyframes fall { to { transform: translateY(320px) rotate(360deg); opacity: 0; } }`}</style>
      <div ref={confettiRef} className="absolute inset-0 pointer-events-none" />
      <div
        className="absolute inset-0 opacity-15 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(#000 1.2px, transparent 1.2px)",
          backgroundSize: "16px 16px",
        }}
      />
      <div className="relative flex items-center gap-4 flex-wrap">
        <div className={`w-24 h-24 rounded-full border-4 border-black flex items-center justify-center flex-shrink-0 ${isGold ? "bg-white" : "bg-canales-blue"}`}>
          <AnimatePresence mode="wait">
            {isGold ? (
              <motion.div key="g" initial={{ scale: 0.4, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}>
                <Award className="w-14 h-14 text-canales-red" />
              </motion.div>
            ) : (
              <motion.div key="s" initial={{ scale: 0.4 }} animate={{ scale: 1 }}>
                <Check className="w-14 h-14 text-white" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className={`text-xs font-display font-black uppercase tracking-widest ${isGold ? "text-black/70" : "text-canales-red"}`}>
            {isGold
              ? (lang === "es" ? "🏆 Badge Dorado" : "🏆 Gold Badge")
              : (lang === "es" ? "🏅 Badge Digital" : "🏅 Digital Badge")}
          </div>
          <div className="font-display font-black text-2xl leading-tight mt-1">
            {isGold
              ? (lang === "es" ? "¡Aprobado y aprendido!" : "Passed & learned!")
              : (lang === "es" ? "Leí y aproveché este cómic" : "I read and learned from this comic")}
          </div>
          <div className="font-body text-sm mt-2">
            <span className="font-bold">{name || (typeof window !== "undefined" ? localStorage.getItem("canales_reader_name") : "")}</span>
            <span className="mx-1">·</span>
            <span>{comic.title[lang]}</span>
            <span className="mx-1">·</span>
            <span className="italic">{dateNow}</span>
          </div>
          {isGold && status?.best_score != null && (
            <div className="mt-1 text-sm font-body">
              {lang === "es" ? "Puntaje del examen" : "Exam score"}:{" "}
              <span className="font-display font-black">{status.best_score}%</span>
            </div>
          )}
          {!isGold && (
            <div className="mt-3">
              <Link
                to={`/exam/${comic.exam_slug}`}
                data-testid={`${testid}-goto-exam`}
                className="inline-flex brutal-btn bg-canales-red text-white px-3 py-1.5 text-sm items-center gap-2"
              >
                <ClipboardCheck className="w-4 h-4" />
                {lang === "es" ? "Pasa el examen y vuélvelo DORADO" : "Pass the exam to turn it GOLD"}
              </Link>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
